import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useAsync from 'react-use/lib/useAsync';
import useField from 'hooks/useField';
import CustomObjectService from 'services/CustomObjectsService';
import MultiSelect from 'components/Inputs/MultiSelect';
import { getSelectedValueLink } from 'components/Inputs/MultiSelect/customize';
import { useRelationshipSelect } from 'components/Inputs/Select/hooks';
import { useSelectTypeaheadWithScroll } from 'components/Inputs/Select/hooks';
import Cols from 'components/Layout/Cols';
import Select from 'components/Inputs/Select';
import ClearSelectButton from 'components/Inputs/Select/ClearButton';
import ApplySelectButton from 'components/Inputs/Select/ApplyButton';
import Loader from 'components/Kizen/Loader';
import { objectNameToOption } from 'services/helpers';
import { SELECTION_SCOPE } from 'constants/fields';
import { useAttachListener } from 'hooks/useAttachListener';
import { applyRef } from 'components/Inputs/props';
import { displayNamesQuery } from 'query/records';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const SelectedEntitiesSelect = ({
  field,
  value,
  onChange,
  onMenuOpen,
  onMenuClose,
}) => {
  const { t } = useTranslation();

  const [{ displayValue, handleChange, relationship }] = useRelationshipSelect({
    value,
    onChange,
    field,
    isMulti: true,
  });

  const chosenValueIds = useMemo(
    () => (value || []).map((item) => item.value ?? item.id),
    [value]
  );

  const selectRef = useRef(null);

  const [typeaheadProps, typeaheadState] = useSelectTypeaheadWithScroll({
    fetch: relationship.search,
    objectToOption: relationship.toOption,
    fieldId: field.id,
    selectRef,
    chosenValueIds,
    relatedObjectId: field.relation.relatedObject,
    onMenuOpen,
    onMenuClose,
  });

  const ObjectLink = useMemo(
    () =>
      relationship.link
        ? getSelectedValueLink({
            genLink: relationship.link,
          })
        : null,
    [relationship.link]
  );

  return (
    <>
      <MultiSelect
        ref={selectRef}
        label={t('Choose Records')}
        value={displayValue}
        onChange={handleChange}
        placeholder={t('Find Records')}
        menuLeftButton={<ClearSelectButton />}
        menuRightButton={<ApplySelectButton />}
        isLoading={typeaheadState?.loading}
        PillComponent={ObjectLink}
        {...typeaheadProps}
      />
    </>
  );
};

// Simplyfied  RelationShip for the activities
export default function ActivityRelationshipOptions({
  updateField,
  updateValidation,
  formData = EMPTY_OBJECT,
  data = EMPTY_OBJECT,
}) {
  const isCreateNew = !data.field;
  const currentRelatedObject = !isCreateNew
    ? data.field.relation.relatedObject
    : null;

  const { t } = useTranslation();
  const currentRelation = formData.relation || EMPTY_OBJECT;

  const [relatedObject, setRelatedObject] = useField(
    currentRelation.relatedObject
  );
  const [cardinality, setCardinality] = useField(
    currentRelation.cardinality || 'one_to_one'
  );
  const [selectionScope, setSelectionScope] = useField(
    currentRelation.selectionScope || SELECTION_SCOPE.ALL_RECORDS
  );
  const [selectedEntities, setSelectedEntities] = useField(
    currentRelation.selectedEntities || EMPTY_ARRAY
  );

  const [releationOptions, selectionScopeOptions] = useMemo(
    () => [
      [
        { value: 'one_to_one', label: t('Choose One') },
        { value: 'one_to_many', label: t('Choose Many') },
      ],
      [
        {
          value: SELECTION_SCOPE.ALL_RECORDS,
          label: t('All Records'),
        },
        {
          value: SELECTION_SCOPE.SELECTED_RECORDS,
          label: t('Selected Records'),
        },
      ],
    ],
    [t]
  );

  // Load models on mount; account-specific, independent of formData,
  // shouldn't need to be reloaded
  const {
    loading,
    value: { models = EMPTY_ARRAY, modelOptions = EMPTY_ARRAY } = {},
  } = useAsync(async () => {
    const models = await CustomObjectService.getAllowedRelations();
    return {
      models,
      modelOptions: models.map(objectNameToOption),
    };
  }, []);

  const fetchUrl = useMemo(
    () => models.find((model) => model.id === relatedObject)?.fetchUrl,
    [relatedObject, models]
  );

  const isValid = Boolean(
    relatedObject &&
      (selectionScope === SELECTION_SCOPE.ALL_RECORDS ||
        selectedEntities.length)
  );

  useEffect(() => {
    updateValidation(isValid);
  }, [isValid, updateValidation]);

  useEffect(() => {
    updateField('relation', {
      relatedObject,
      fetchUrl,
      cardinality,
      selectedEntities:
        selectionScope === SELECTION_SCOPE.SELECTED_RECORDS
          ? selectedEntities
          : EMPTY_ARRAY,
      selectionScope,
    });
  }, [
    cardinality,
    relatedObject,
    fetchUrl,
    updateField,
    selectionScope,
    selectedEntities,
  ]);

  const handleSelectRelatedObject = useCallback(
    (opt) => {
      setRelatedObject(opt.value);
      setSelectionScope(SELECTION_SCOPE.ALL_RECORDS);
      setSelectedEntities(EMPTY_ARRAY);
    },
    [setRelatedObject, setSelectionScope, setSelectedEntities]
  );

  const handleSelectRelationType = useCallback(
    (opt) => {
      setCardinality(opt.value);
    },
    [setCardinality]
  );

  const handleSelectSelectionScope = useCallback(
    (opt) => {
      setSelectionScope(opt.value);
      if (opt.value === SELECTION_SCOPE.ALL_RECORDS) {
        setSelectedEntities(EMPTY_ARRAY);
      }
    },
    [setSelectedEntities, setSelectionScope]
  );

  const menuOpenRef = useRef(false);
  const sectionRef = useRef(null);

  const scrollDropdownListener = useCallback(() => {
    if (menuOpenRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const listenerRef = useAttachListener(scrollDropdownListener, 'mouseup');
  const mergeRef = (el) => {
    applyRef(listenerRef, el);
    applyRef(sectionRef, el);
  };

  const onMenuOpen = useCallback(() => {
    menuOpenRef.current = true;
  }, []);
  const onMenuClose = useCallback(() => {
    menuOpenRef.current = false;
  }, []);

  const deletedObjectQuery = useQuery(
    displayNamesQuery(
      { custom_object_ids: [currentRelatedObject] },
      {
        enabled: Boolean(currentRelatedObject),
        staleTime: Infinity,
        select: (data) => {
          return data.not_found.custom_objects[0];
        },
      }
    )
  );

  const showDeletedOption =
    deletedObjectQuery.data && relatedObject === currentRelatedObject;

  const optionsWithDeleted = useMemo(() => {
    if (!showDeletedOption) {
      return modelOptions;
    }

    const deletedOption = {
      value: deletedObjectQuery.data.id,
      label: `[${t('Deleted')}] ${deletedObjectQuery.data.display_name}`,
    };

    return [deletedOption].concat(modelOptions);
  }, [t, showDeletedOption, modelOptions, deletedObjectQuery.data]);

  return (
    <Loader loading={loading}>
      <div ref={mergeRef}>
        <Cols columns={2} gutter="20px">
          <Select
            label={t('Related Entity')}
            placeholder={t('Choose Related Entity')}
            value={relatedObject}
            options={optionsWithDeleted}
            onChange={handleSelectRelatedObject}
            disabled={!isCreateNew && !deletedObjectQuery.data}
            margin
            onMenuOpen={onMenuOpen}
            onMenuClose={onMenuClose}
          />
          {relatedObject ? (
            <Select
              label={t('Relationship Type')}
              value={cardinality}
              options={releationOptions}
              onChange={handleSelectRelationType}
              margin
              onMenuOpen={onMenuOpen}
              onMenuClose={onMenuClose}
            />
          ) : null}
        </Cols>
        {relatedObject && !showDeletedOption ? (
          <Cols columns={2} gutter="20px">
            <Select
              label={t('Record Selection Scope')}
              value={selectionScope}
              options={selectionScopeOptions}
              onChange={handleSelectSelectionScope}
              margin
              onMenuOpen={onMenuOpen}
              onMenuClose={onMenuClose}
            />
            {selectionScope === SELECTION_SCOPE.ALL_RECORDS ? null : (
              <>
                <SelectedEntitiesSelect
                  key={relatedObject}
                  field={formData}
                  value={selectedEntities}
                  onChange={setSelectedEntities}
                  onMenuOpen={onMenuOpen}
                  onMenuClose={onMenuClose}
                />
              </>
            )}
          </Cols>
        ) : null}
      </div>
    </Loader>
  );
}
